/**
 * DocumentsPage.js - Gestão de Documentos Comerciais
 * Cotações, Guias de Entrega, Guias de Recepção
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Eye, Printer, Download, Send,
  CheckCircle, XCircle, Clock, ShoppingCart, FileCheck,
  Receipt, Package, ArrowRight, UserPlus, RefreshCcw
} from 'lucide-react';
import { ProductCombobox } from '@/components/ProductCombobox';
import { formatCurrency, formatDateTime, formatDisplayOnlyCode, cn } from '@/lib/utils';
import { productsAPI, settingsAPI, clientsAPI, vatAPI, printAPI, servicesAPI, salesAPI, getErrorMessage } from '@/lib/api';
import axios from 'axios';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { useAuth } from '@/hooks/useAuth';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import { useLanguage } from '@/i18n';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';
import { getStoredAuthToken } from '@/lib/authStorage';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const documentsAPI = {
  // Quotations
  listQuotations: (params) => api.get('/api/documents/quotations', { params }),
  getQuotation: (id) => api.get(`/api/documents/quotations/${id}`),
  createQuotation: (data) => api.post('/api/documents/quotations', data),
  updateQuotation: (id, data) => api.put(`/api/documents/quotations/${id}`, data),
  approveQuotation: (id) => api.post(`/api/documents/quotations/${id}/approve`),
  bulkQuotationStatus: (data) => api.post('/api/documents/quotations/bulk-status', data),
  convertToSale: (id, data) => api.post(`/api/documents/quotations/${id}/convert-to-sale`, data),
  // PDF Generation
  getQuotationPdfUrl: (id, copyType = 'Original') => `${API_BASE_URL}/api/documents/quotations/${id}/pdf?copy_type=${copyType}`,
  getReceptionGuidePdfUrl: (id, copyType = 'Original') => `${API_BASE_URL}/api/documents/reception-guides/${id}/pdf?copy_type=${copyType}`,
  getDeliveryGuidePdfUrl: (id, copyType = 'Original') => `${API_BASE_URL}/api/documents/delivery-guides/${id}/pdf?copy_type=${copyType}`,
  // Reception Guides
  listReceptionGuides: (params) => api.get('/api/documents/reception-guides', { params }),
  createReceptionGuide: (data) => api.post('/api/documents/reception-guides', data),
  // Delivery Guides
  listDeliveryGuides: (params) => api.get('/api/documents/delivery-guides', { params }),
  createDeliveryGuide: (data) => api.post('/api/documents/delivery-guides', data),
  // Service Invoices
  listServiceInvoices: (params) => api.get('/api/documents/service-invoices', { params }),
  // Statistics
  getStatistics: (params) => api.get('/api/documents/statistics', { params })
};

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  converted: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-gray-100 text-gray-700',
  expired: 'bg-orange-100 text-orange-700'
};

const statusLabels = {
  draft: 'Rascunho',
  pending: 'Pendente',
  approved: 'Aprovado',
  sent: 'Enviado',
  accepted: 'Aceite',
  rejected: 'Rejeitado',
  converted: 'Convertido',
  cancelled: 'Cancelado',
  expired: 'Expirado'
};

const quotationDocTypeOptions = [
  { value: 'vd', label: 'VD (Venda a Dinheiro)' },
  { value: 'factura', label: 'FACTURA' },
  { value: 'proforma', label: 'PROFORMA' }
];

const DOCUMENT_TABS = ['quotations', 'reception', 'delivery', 'invoices', 'credit-notes'];
const DEFAULT_VAT_CONFIG = { default_rate: 16.0, default_regime: 'inclusive' };
const INVENTORY_TABLE_HEAD_CLASS = 'px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500';
const INVENTORY_TABLE_CELL_CLASS = 'px-3 py-2.5 align-top';
const INVENTORY_TABLE_ROW_CLASS = 'group border-b border-slate-100 transition-all hover:bg-indigo-50/35';

const getDocumentItemKey = (item, index, prefix = 'document-item') => {
  const stableParts = [
    item?.item_id,
    item?.product_id,
    item?.sku,
    item?.document_item_id,
    item?.description,
    item?.product_name,
    item?.quantity,
    item?.unit_price,
  ].filter(Boolean);

  return stableParts.length > 0 ? `${prefix}-${stableParts.join('-')}` : `${prefix}-${index}`;
};

const getDocumentDisplayNumber = (document) => {
  return document?.document_number
    || document?.invoice_number
    || document?.service_number
    || document?.sale_number
    || document?.nc_number
    || document?.fiscal_document_number
    || 'Documento';
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { defaultDocumentLocationId, setDefaultDocumentLocation } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(DOCUMENT_TABS.includes(urlTab) ? urlTab : 'quotations');
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotationIds, setSelectedQuotationIds] = useState([]);
  const [selectedReceptionGuideIds, setSelectedReceptionGuideIds] = useState([]);
  const [selectedDeliveryGuideIds, setSelectedDeliveryGuideIds] = useState([]);
  const [selectedServiceInvoiceIds, setSelectedServiceInvoiceIds] = useState([]);
  const [selectedCreditNoteIds, setSelectedCreditNoteIds] = useState([]);
  const [receptionGuides, setReceptionGuides] = useState([]);
  const [deliveryGuides, setDeliveryGuides] = useState([]);
  const [serviceInvoices, setServiceInvoices] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [salesDocuments, setSalesDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location_id') || 'all');
  const [creditNoteStatusFilter, setCreditNoteStatusFilter] = useState('all');
  const [creditNoteRefundFilter, setCreditNoteRefundFilter] = useState('all');
  const [creditNoteSourceFilter, setCreditNoteSourceFilter] = useState('all');
  const docsSyncSnapshotRef = useRef('');
  
  // Form states
  const [quotationDialogOpen, setQuotationDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [quotationToConvert, setQuotationToConvert] = useState(null);
  const [conversionForm, setConversionForm] = useState({
    document_type: 'vd',
    location_id: '',
    items: []
  });
  const [convertingQuotation, setConvertingQuotation] = useState(false);
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [creatingCreditNote, setCreatingCreditNote] = useState(false);
  const [creditNoteForm, setCreditNoteForm] = useState({
    source_type: 'sale',
    source_id: '',
    reason: '',
    refund_method: 'credit',
    note_type: 'partial_return',
    restock_items: true,
    items: [],
    notes: ''
  });
  const [newClientForm, setNewClientForm] = useState({
    name: '',
    phone: '',
    email: '',
    nuit: '',
    address: '',
    type: 'individual'
  });
  const [savingClient, setSavingClient] = useState(false);
  
  // Data for forms
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [paymentConditions, setPaymentConditions] = useState([]);
  const [locations, setLocations] = useState([]);

  // Download PDF function
  const downloadPdf = async (docType, doc, copyType = 'Original') => {
    try {
      const docId = doc?.id;
      const selectedLocationId = locationFilter !== 'all' ? locationFilter : (doc?.location_id || defaultDocumentLocationId || null);

      if (docType === 'quotation') {
        const result = await printAPI.renderDocumentPdf({
          doc_type: 'quotation',
          quotation_id: docId,
          location_id: selectedLocationId,
          module_group: 'DOCUMENTOS',
          copy_type: copyType
        }, 'download');

        if (result?.success) {
          toast.success(`PDF ${copyType} gerado com sucesso`);
        } else {
          toast.error(result?.error || 'Erro ao gerar PDF de cotação');
        }
        return;
      }

      if (docType === 'reception') {
        if (!doc?.service_id) {
          toast.error('Serviço não encontrado para esta guia de recepção');
          return;
        }
        const result = await printAPI.renderDocumentPdf({
          doc_type: 'reception_guide',
          service_id: doc.service_id,
          location_id: selectedLocationId,
          module_group: 'SERVIÇOS',
          copy_type: copyType,
        }, 'download');
        if (result?.success) {
          toast.success(`PDF ${copyType} gerado com sucesso`);
        } else {
          toast.error(result?.error || 'Erro ao gerar PDF da guia de recepção');
        }
        return;
      }

      if (docType === 'delivery') {
        const payload = {
          copy_type: copyType,
          location_id: doc.location_id
        };
        if (doc?.sale_id) payload.sale_id = doc.sale_id;
        if (!doc?.sale_id && doc?.service_id) payload.service_id = doc.service_id;

        if (!payload.sale_id && !payload.service_id) {
          toast.error('Origem não encontrada para esta guia de entrega');
          return;
        }

        if (doc?.service_id && !doc?.sale_id) {
          const result = await printAPI.renderDocumentPdf({
            doc_type: 'service_guide',
            service_id: doc.service_id,
            location_id: selectedLocationId,
            module_group: 'SERVIÇOS',
            copy_type: copyType,
          }, 'download');
          if (result?.success) {
            toast.success(`PDF ${copyType} gerado com sucesso`);
          } else {
            toast.error(result?.error || 'Erro ao gerar guia de serviço');
          }
        } else {
          const result = await printAPI.renderDocumentPdf({
            ...payload,
            doc_type: 'delivery_guide',
            module_group: 'VENDAS',
          }, 'download');
          if (result?.success) {
            toast.success(`PDF ${copyType} gerado com sucesso`);
          } else {
            toast.error(result?.error || 'Erro ao gerar guia de entrega');
          }
        }
        return;
      }

      if (docType === 'service_invoice') {
        if (!doc?.service_id && !doc?.sale_id) {
          toast.error('Documento de origem não encontrado para esta factura');
          return;
        }

        const payload = {
          doc_type: 'invoice',
          location_id: selectedLocationId,
          module_group: 'SERVIÇOS',
          copy_type: copyType,
        };
        if (doc?.service_id) payload.service_id = doc.service_id;
        if (!doc?.service_id && doc?.sale_id) payload.sale_id = doc.sale_id;

        const result = await printAPI.renderDocumentPdf(payload, 'download');
        if (result?.success) {
          toast.success(`Factura ${copyType} descarregada com sucesso`);
        } else {
          toast.error(result?.error || 'Erro ao gerar factura');
        }
        return;
      }

      throw new Error('Unknown document type');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Erro ao gerar PDF');
    }
  };
  
  const [quotationForm, setQuotationForm] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_nuit: '',
    customer_address: '',
    valid_until: '',
    payment_condition_id: '',
    location_id: '',
    items: [],
    notes: '',
    terms: '',
    vat_regime: 'inclusive'  // IVA regime for the quotation
  });
  
  // VAT Configuration state
  const [vatConfig, setVatConfig] = useState(DEFAULT_VAT_CONFIG);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const effectiveVatConfig = vatConfig || DEFAULT_VAT_CONFIG;
  const requestedLocationHandle = searchParams.get('location');
  const locationHandleMaps = useMemo(() => buildHandleMaps(locations, (location) => location.name), [locations]);
  const receivablesHref = useMemo(() => {
    if (locationFilter === 'all') {
      return '/receivables';
    }

    const params = new URLSearchParams();
    applyCleanOrLegacyParam({
      params,
      cleanKey: 'location',
      legacyKey: 'location_id',
      value: locationFilter,
      idToHandle: locationHandleMaps.idToHandle,
    });

    const query = params.toString();
    return query ? `/receivables?${query}` : '/receivables';
  }, [locationFilter, locationHandleMaps.idToHandle]);
  const getPreferredStoreLocationId = useCallback((fallbackId = '') => {
    if (fallbackId && locations.some((location) => location.id === fallbackId)) {
      return fallbackId;
    }
    if (locationFilter !== 'all' && locations.some((location) => location.id === locationFilter)) {
      return locationFilter;
    }
    if (defaultDocumentLocationId && locations.some((location) => location.id === defaultDocumentLocationId)) {
      return defaultDocumentLocationId;
    }
    return locations[0]?.id || '';
  }, [defaultDocumentLocationId, locationFilter, locations]);

  useEffect(() => {
    const urlTabValue = searchParams.get('tab');
    const normalizedUrlTab = DOCUMENT_TABS.includes(urlTabValue) ? urlTabValue : 'quotations';
    const urlSearch = searchParams.get('q') || '';
    const urlLocation = resolveHandleOrLegacyId({
      cleanValue: searchParams.get('location'),
      legacyValue: searchParams.get('location_id') || 'all',
      handleToId: locationHandleMaps.handleToId,
    }) || 'all';
    const snapshot = `${normalizedUrlTab}|${urlSearch}|${urlLocation}`;

    if (docsSyncSnapshotRef.current === snapshot) return;
    docsSyncSnapshotRef.current = snapshot;

    setActiveTab((prev) => (prev === normalizedUrlTab ? prev : normalizedUrlTab));
    setSearchTerm((prev) => (prev === urlSearch ? prev : urlSearch));
    setLocationFilter((prev) => {
      if (!searchParams.get('location') && !searchParams.get('location_id') && prev !== 'all') {
        return prev;
      }
      return prev === urlLocation ? prev : urlLocation;
    });
  }, [searchParams, locationHandleMaps.handleToId]);

  useEffect(() => {
    if (!requestedLocationHandle || !locations.length) return;
    const resolvedLocationId = resolveHandleOrLegacyId({
      cleanValue: requestedLocationHandle,
      legacyValue: searchParams.get('location_id') || 'all',
      handleToId: locationHandleMaps.handleToId,
    });

    if (resolvedLocationId && locationFilter === 'all') {
      setLocationFilter(resolvedLocationId);
    }
  }, [locationFilter, locationHandleMaps.handleToId, locations.length, requestedLocationHandle, searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', activeTab);

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) params.set('q', trimmedSearch);
    else params.delete('q');

    params.delete('location');
    params.delete('location_id');
    if (locationFilter && locationFilter !== 'all') {
      const hasLocationHandles = Object.keys(locationHandleMaps.idToHandle || {}).length > 0;
      if (!hasLocationHandles && searchParams.get('location_id') === locationFilter) {
        params.set('location_id', locationFilter);
      } else {
        applyCleanOrLegacyParam({
          params,
          cleanKey: 'location',
          legacyKey: 'location_id',
          value: locationFilter,
          idToHandle: locationHandleMaps.idToHandle,
        });
      }
    }

    const stateSnapshot = `${activeTab}|${trimmedSearch}|${locationFilter || 'all'}`;

    if (params.toString() !== searchParams.toString()) {
      docsSyncSnapshotRef.current = stateSnapshot;
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchTerm, locationFilter, searchParams, setSearchParams, locationHandleMaps.idToHandle]);

  const handleLocationFilterChange = (nextLocationId) => {
    setLocationFilter(nextLocationId);
    if (nextLocationId && nextLocationId !== 'all') {
      const selectedLocation = locations.find((loc) => loc.id === nextLocationId);
      setDefaultDocumentLocation({ id: nextLocationId, name: selectedLocation?.name || '' });
    }
  };

  const loadDocuments = useCallback(async () => {
    try {
      if (activeTab === 'quotations') {
        const res = await documentsAPI.listQuotations({ limit: 100 });
        setQuotations(res.data.items || []);
      } else if (activeTab === 'reception') {
        const res = await documentsAPI.listReceptionGuides({ limit: 100 });
        setReceptionGuides(res.data.items || []);
      } else if (activeTab === 'delivery') {
        const res = await documentsAPI.listDeliveryGuides({ limit: 100 });
        setDeliveryGuides(res.data.items || []);
      } else if (activeTab === 'invoices') {
        const res = await documentsAPI.listServiceInvoices({ limit: 200 });
        setServiceInvoices(res.data.items || []);
      } else if (activeTab === 'credit-notes') {
        const token = getStoredAuthToken();
        const params = new URLSearchParams();
        if (locationFilter !== 'all') params.set('location_id', locationFilter);
        if (creditNoteStatusFilter !== 'all') params.set('status', creditNoteStatusFilter);
        if (creditNoteRefundFilter !== 'all') params.set('refund_method', creditNoteRefundFilter);
        if (creditNoteSourceFilter !== 'all') params.set('source_type', creditNoteSourceFilter);
        const queryString = params.toString();
        const response = await fetch(`${API_BASE_URL}/api/fiscal/notas-credito${queryString ? `?${queryString}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        setCreditNotes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  }, [activeTab, creditNoteRefundFilter, creditNoteSourceFilter, creditNoteStatusFilter, locationFilter]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const [productsRes, clientsRes, locationsRes, statsRes, vatConfigRes, paymentConditionsRes, salesRes, creditNotesRes] = await Promise.all([
        safeRequest(() => productsAPI.list({ limit: 500 }), { data: { items: [] } }, { telemetry: { module: 'documents', endpoint: 'products.list', onEvent: captureFetchTelemetry } }),
        safeRequest(() => clientsAPI.list({ limit: 500 }), { data: { items: [] } }, { telemetry: { module: 'documents', endpoint: 'clients.list', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listLocationsByType('store'), { data: [] }, { telemetry: { module: 'documents', endpoint: 'settings.store-locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => documentsAPI.getStatistics({ period: 'this_month' }), { data: null }, { telemetry: { module: 'documents', endpoint: 'documents.statistics', onEvent: captureFetchTelemetry } }),
        safeRequest(() => vatAPI.getConfig(), { data: DEFAULT_VAT_CONFIG }, { telemetry: { module: 'documents', endpoint: 'vat.config', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listPaymentConditions(), { data: [] }, { telemetry: { module: 'documents', endpoint: 'settings.payment_conditions', onEvent: captureFetchTelemetry } }),
        safeRequest(() => salesAPI.list({ include_cancelled: false }), { data: [] }, { telemetry: { module: 'documents', endpoint: 'sales.list', onEvent: captureFetchTelemetry } }),
        safeRequest(() => {
          const token = getStoredAuthToken();
          return fetch(`${API_BASE_URL}/api/fiscal/notas-credito`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }).then(r => r.json());
        }, [], { telemetry: { module: 'documents', endpoint: 'fiscal.credit_notes', onEvent: captureFetchTelemetry } })
      ]);
      
      const productsData = productsRes.data;
      setProducts(Array.isArray(productsData) ? productsData : (productsData.items || []));

      const clientsData = clientsRes.data;
      setClients(Array.isArray(clientsData) ? clientsData : (clientsData.items || []));

      const storeLocations = (locationsRes.data || []).filter((location) => location.is_deleted !== true && location.is_hidden !== true && location.is_active !== false);
      setLocations(storeLocations);
      setStatistics(statsRes.data);

      const preferredStoreLocationId = (
        (locationFilter !== 'all' && storeLocations.some((location) => location.id === locationFilter) && locationFilter)
        || (defaultDocumentLocationId && storeLocations.some((location) => location.id === defaultDocumentLocationId) && defaultDocumentLocationId)
        || storeLocations[0]?.id
        || 'all'
      );
      if (locationFilter !== 'all' && !storeLocations.some((location) => location.id === locationFilter) && preferredStoreLocationId !== 'all') {
        setLocationFilter(preferredStoreLocationId);
      }

      const nextVatConfig = vatConfigRes.data || DEFAULT_VAT_CONFIG;
      setVatConfig(nextVatConfig);
      setQuotationForm((prev) => ({
        ...prev,
        vat_regime: nextVatConfig.default_regime || DEFAULT_VAT_CONFIG.default_regime,
        location_id: prev.location_id && storeLocations.some((location) => location.id === prev.location_id)
          ? prev.location_id
          : (preferredStoreLocationId !== 'all' ? preferredStoreLocationId : ''),
      }));
      setPaymentConditions(paymentConditionsRes.data || []);
      setSalesDocuments(salesRes.data || []);
      setCreditNotes(creditNotesRes.data || creditNotesRes || []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, defaultDocumentLocationId, locationFilter, resetFetchTelemetry]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const normalizeSearch = useCallback((value) => (value || '').toString().toLowerCase(), []);

  const filteredQuotations = useMemo(() => {
    const term = normalizeSearch(searchTerm);
    return quotations.filter((quotation) => {
      if (locationFilter !== 'all' && quotation.location_id !== locationFilter) return false;
      if (!term) return true;
      return [
        quotation.document_number,
        quotation.customer_name,
        quotation.created_by_name,
        quotation.id,
      ].some((field) => normalizeSearch(field).includes(term));
    });
  }, [quotations, locationFilter, searchTerm, normalizeSearch]);

  useEffect(() => {
    setSelectedQuotationIds([]);
    setSelectedReceptionGuideIds([]);
    setSelectedDeliveryGuideIds([]);
    setSelectedServiceInvoiceIds([]);
    setSelectedCreditNoteIds([]);
  }, [activeTab, locationFilter, searchTerm, filteredQuotations.length]);

  const filteredReceptionGuides = useMemo(() => {
    const term = normalizeSearch(searchTerm);
    return receptionGuides.filter((guide) => {
      if (locationFilter !== 'all' && guide.location_id !== locationFilter) return false;
      if (!term) return true;
      return [guide.document_number, guide.customer_name, guide.service_number, guide.id]
        .some((field) => normalizeSearch(field).includes(term));
    });
  }, [receptionGuides, locationFilter, searchTerm, normalizeSearch]);

  const filteredDeliveryGuides = useMemo(() => {
    const term = normalizeSearch(searchTerm);
    return deliveryGuides.filter((guide) => {
      if (locationFilter !== 'all' && guide.location_id !== locationFilter) return false;
      if (!term) return true;
      return [guide.document_number, guide.customer_name, guide.sale_number, guide.id]
        .some((field) => normalizeSearch(field).includes(term));
    });
  }, [deliveryGuides, locationFilter, searchTerm, normalizeSearch]);

  const filteredServiceInvoices = useMemo(() => {
    const term = normalizeSearch(searchTerm);
    return serviceInvoices.filter((invoice) => {
      if (locationFilter !== 'all' && invoice.location_id !== locationFilter) return false;
      if (!term) return true;
      return [
        invoice.invoice_number,
        invoice.service_number,
        invoice.customer_name,
        invoice.id,
      ].some((field) => normalizeSearch(field).includes(term));
    });
  }, [serviceInvoices, locationFilter, searchTerm, normalizeSearch]);

  const filteredCreditNotes = useMemo(() => {
    const term = normalizeSearch(searchTerm);
    return creditNotes.filter((note) => {
      if (locationFilter !== 'all' && note.location_id !== locationFilter) return false;
      if (creditNoteStatusFilter !== 'all' && note.status !== creditNoteStatusFilter) return false;
      if (creditNoteRefundFilter !== 'all' && note.refund_method !== creditNoteRefundFilter) return false;
      if (creditNoteSourceFilter !== 'all' && note.source_type !== creditNoteSourceFilter) return false;
      if (!term) return true;
      return [note.nc_number, note.original_document_number, note.customer_name, note.reason, note.id, note.source_type, note.refund_method]
        .some((field) => normalizeSearch(field).includes(term));
    });
  }, [creditNoteRefundFilter, creditNoteSourceFilter, creditNoteStatusFilter, creditNotes, locationFilter, searchTerm, normalizeSearch]);

  const creditNoteStatusMeta = useMemo(() => ({
    pending: { label: t('documents.creditNotes.statuses.pending'), className: 'bg-amber-100 text-amber-700 border-amber-200' },
    approved: { label: t('documents.creditNotes.statuses.approved'), className: 'bg-blue-100 text-blue-700 border-blue-200' },
    processed: { label: t('documents.creditNotes.statuses.processed'), className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    rejected: { label: t('documents.creditNotes.statuses.rejected'), className: 'bg-rose-100 text-rose-700 border-rose-200' },
    cancelled: { label: t('documents.creditNotes.statuses.cancelled'), className: 'bg-slate-100 text-slate-700 border-slate-200' },
  }), [t]);

  const creditNoteRefundMeta = useMemo(() => ({
    credit: { label: t('documents.creditNotes.refundMethods.credit'), className: 'bg-purple-100 text-purple-700 border-purple-200' },
    cash: { label: t('documents.creditNotes.refundMethods.cash'), className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    bank_transfer: { label: t('documents.creditNotes.refundMethods.bank_transfer'), className: 'bg-sky-100 text-sky-700 border-sky-200' },
    replacement: { label: t('documents.creditNotes.refundMethods.replacement'), className: 'bg-orange-100 text-orange-700 border-orange-200' },
  }), [t]);

  const creditNoteSourceMeta = useMemo(() => ({
    sale: { label: t('documents.creditNotes.sourceTypes.sale'), className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    service_invoice: { label: t('documents.creditNotes.sourceTypes.service_invoice'), className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  }), [t]);

  const creditNoteSummary = useMemo(() => ({
    total: creditNotes.length,
    pending: creditNotes.filter((note) => note.status === 'pending').length,
    approved: creditNotes.filter((note) => note.status === 'approved').length,
    processed: creditNotes.filter((note) => note.status === 'processed').length,
  }), [creditNotes]);

  const creditNoteOperationalSummary = useMemo(() => ({
    visible: filteredCreditNotes.length,
    pendingActions: filteredCreditNotes.filter((note) => note.status === 'pending' || note.status === 'approved').length,
    totalAmount: filteredCreditNotes.reduce((sum, note) => sum + Number(note.total_amount || 0), 0),
    customerCredit: filteredCreditNotes.filter((note) => note.refund_method === 'credit').length,
  }), [filteredCreditNotes]);

  const creditNoteLocationNameById = useMemo(() => {
    return new Map(locations.map((location) => [location.id, location.name]));
  }, [locations]);

  const activeCreditNoteFilters = useMemo(() => {
    const filtersList = [];
    if (creditNoteStatusFilter !== 'all') filtersList.push(t(`documents.creditNotes.statuses.${creditNoteStatusFilter}`));
    if (creditNoteRefundFilter !== 'all') filtersList.push(t(`documents.creditNotes.refundMethods.${creditNoteRefundFilter}`));
    if (creditNoteSourceFilter !== 'all') filtersList.push(t(`documents.creditNotes.sourceTypes.${creditNoteSourceFilter}`));
    if (locationFilter !== 'all') filtersList.push(creditNoteLocationNameById.get(locationFilter) || locationFilter);
    return filtersList;
  }, [creditNoteLocationNameById, creditNoteRefundFilter, creditNoteSourceFilter, creditNoteStatusFilter, locationFilter, t]);

  const resetCreditNoteFilters = useCallback(() => {
    setCreditNoteStatusFilter('all');
    setCreditNoteRefundFilter('all');
    setCreditNoteSourceFilter('all');
    handleLocationFilterChange('all');
  }, [handleLocationFilterChange]);

  const applyCreditNoteQuickFilter = useCallback((preset) => {
    if (preset === 'clear') {
      resetCreditNoteFilters();
      return;
    }

    setCreditNoteStatusFilter('all');
    setCreditNoteRefundFilter('all');
    setCreditNoteSourceFilter('all');

    if (preset === 'pending') setCreditNoteStatusFilter('pending');
    if (preset === 'approved') setCreditNoteStatusFilter('approved');
    if (preset === 'customerCredit') setCreditNoteRefundFilter('credit');
    if (preset === 'saleOrigin') setCreditNoteSourceFilter('sale');
    if (preset === 'serviceOrigin') setCreditNoteSourceFilter('service_invoice');
  }, [resetCreditNoteFilters]);

  const getCreditNoteNextStepLabel = useCallback((status) => {
    if (status === 'pending') return t('documents.creditNotes.nextSteps.approve');
    if (status === 'approved') return t('documents.creditNotes.nextSteps.process');
    if (status === 'processed') return t('documents.creditNotes.nextSteps.done');
    return t('documents.creditNotes.nextSteps.review');
  }, [t]);

  const eligibleSalesForCreditNotes = useMemo(() => {
    return (salesDocuments || []).filter((sale) => sale.status !== 'draft' && sale.status !== 'cancelled');
  }, [salesDocuments]);

  const creditNoteSourceOptions = useMemo(() => {
    if (creditNoteForm.source_type === 'service_invoice') {
      return filteredServiceInvoices.map((invoice) => ({
        id: invoice.id,
        label: `${invoice.invoice_number || invoice.fiscal_document_number || invoice.id} · ${invoice.customer_name || 'Cliente'}`,
        payload: invoice,
      }));
    }

    return eligibleSalesForCreditNotes.map((sale) => ({
      id: sale.id,
      label: `${sale.fiscal_document_number || sale.sale_number || sale.id} · ${sale.customer_name || 'Cliente'}`,
      payload: sale,
    }));
  }, [creditNoteForm.source_type, filteredServiceInvoices, eligibleSalesForCreditNotes]);

  const openCreditNoteDialog = (sourceType = 'sale', sourceDoc = null) => {
    const sourceId = sourceDoc?.id || '';
    const sourceItems = (sourceDoc?.items || []).map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name || item.description,
      quantity: item.quantity || 1,
      unit_price: item.unit_price || item.total || 0,
      item_id: item.item_id || null,
      selected: true,
    }));

    setCreditNoteForm({
      source_type: sourceType,
      source_id: sourceId,
      reason: '',
      refund_method: 'credit',
      note_type: 'partial_return',
      restock_items: true,
      items: sourceItems,
      notes: ''
    });
    setCreditNoteDialogOpen(true);
  };

  const handleCreditNoteSourceChange = (sourceId) => {
    const sourceDoc = creditNoteSourceOptions.find((item) => item.id === sourceId)?.payload;
    const sourceItems = (sourceDoc?.items || []).map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name || item.description,
      quantity: item.quantity || 1,
      unit_price: item.unit_price || item.total || 0,
      item_id: item.item_id || null,
      selected: true,
    }));

    setCreditNoteForm((prev) => ({ ...prev, source_id: sourceId, items: sourceItems }));
  };

  const handleCreditNoteItemChange = (index, field, value) => {
    setCreditNoteForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
    }));
  };

  const handleCreateCreditNote = async () => {
    if (!creditNoteForm.source_id || !creditNoteForm.reason.trim()) {
      toast.error('Selecione o documento de origem e informe o motivo da nota de crédito');
      return;
    }

    const selectedItems = creditNoteForm.items.filter((item) => item.selected && Number(item.quantity) > 0);
    if (!selectedItems.length) {
      toast.error('Selecione pelo menos um artigo para a nota de crédito');
      return;
    }

    const sourceDoc = creditNoteSourceOptions.find((item) => item.id === creditNoteForm.source_id)?.payload;
    setCreatingCreditNote(true);
    try {
      const token = getStoredAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/fiscal/nota-credito`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          original_document_id: creditNoteForm.source_id,
          original_document_number: sourceDoc?.fiscal_document_number || sourceDoc?.invoice_number || sourceDoc?.sale_number || sourceDoc?.document_number,
          reason: creditNoteForm.reason,
          refund_method: creditNoteForm.refund_method,
          note_type: creditNoteForm.note_type,
          restock_items: creditNoteForm.restock_items,
          notes: creditNoteForm.notes,
          items: selectedItems.map((item) => ({
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price || 0),
            item_id: item.item_id || null,
          }))
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.detail || 'Erro ao emitir nota de crédito');
      toast.success(data.message || 'Nota de crédito emitida com sucesso');
      setCreditNoteDialogOpen(false);
      if (activeTab === 'credit-notes') await loadDocuments();
      else setActiveTab('credit-notes');
    } catch (error) {
      toast.error(error.message || 'Erro ao emitir nota de crédito');
    } finally {
      setCreatingCreditNote(false);
    }
  };

  const handleApproveCreditNote = async (noteId) => {
    const token = getStoredAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/fiscal/nota-credito/${noteId}/aprovar`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    const data = await response.json();
    if (!response.ok) {
      toast.error(data?.detail || 'Erro ao aprovar nota de crédito');
      return;
    }
    toast.success(data.message || 'Nota de crédito aprovada');
    await loadDocuments();
  };

  const handleProcessCreditNote = async (noteId) => {
    const token = getStoredAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/fiscal/nota-credito/${noteId}/processar`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    const data = await response.json();
    if (!response.ok) {
      toast.error(data?.detail || 'Erro ao processar nota de crédito');
      return;
    }
    toast.success(data.message || 'Nota de crédito processada');
    await loadDocuments();
  };

  const downloadCreditNotePDF = async (creditNoteId, copyType = 'Original') => {
    const result = await printAPI.renderDocumentPdf({ doc_type: 'credit_note', credit_note_id: creditNoteId, copy_type: copyType }, 'download');
    if (!result?.success) {
      toast.error(result?.error || 'Erro ao gerar nota de crédito');
    }
  };

  useEffect(() => {
    const linkedQuotationId = searchParams.get('quote') || searchParams.get('quotation_id');
    if (!linkedQuotationId) return;

    if (activeTab !== 'quotations') {
      setActiveTab('quotations');
      return;
    }

    const fromList = quotations.find((q) => q.id === linkedQuotationId || q.quotation_number === linkedQuotationId);
    if (fromList) {
      setSelectedDocument(fromList);
      setDetailDialogOpen(true);
      const params = new URLSearchParams(searchParams);
      params.delete('quote');
      params.delete('quotation_id');
      setSearchParams(params, { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await documentsAPI.getQuotation(linkedQuotationId);
        setSelectedDocument(res.data);
        setDetailDialogOpen(true);
        const params = new URLSearchParams(searchParams);
        params.delete('quote');
        params.delete('quotation_id');
        setSearchParams(params, { replace: true });
      } catch {
        // noop
      }
    })();
  }, [activeTab, quotations, searchParams, setSearchParams]);

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setQuotationForm(prev => ({
        ...prev,
        customer_id: clientId,
        customer_name: client.name,
        customer_phone: client.phone || '',
        customer_email: client.email || '',
        customer_nuit: client.nuit || '',
        customer_address: client.address || ''
      }));
    }
  };

  const handleAddItem = () => {
    setQuotationForm(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: '',
        description: '',
        sku: '',
        quantity: 1,
        unit_price: 0,
        discount_percent: 0,
        discount_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        subtotal: 0,
        total: 0
      }]
    }));
  };

  const handleRemoveItem = (index) => {
    setQuotationForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setQuotationForm(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // If product selected, fill details
      if (field === 'product_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].description = product.name;
          newItems[index].sku = product.sku;
          newItems[index].unit_price = product.unit_price;
        }
      }
      
      // Recalculate totals
      const item = newItems[index];
      const qty = parseInt(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      const discountPct = parseFloat(item.discount_percent) || 0;
      const taxRate = parseFloat(item.tax_rate) || 0;
      
      item.subtotal = qty * price;
      item.discount_amount = item.subtotal * (discountPct / 100);
      item.tax_amount = (item.subtotal - item.discount_amount) * (taxRate / 100);
      item.total = item.subtotal - item.discount_amount + item.tax_amount;
      
      return { ...prev, items: newItems };
    });
  };

  // VAT Calculation helper
  const calculateVAT = (price, rate, regime) => {
    const vatRate = rate / 100;
    if (regime === 'exempt') {
      return { base: price, vat: 0, total: price };
    }
    if (regime === 'inclusive') {
      const base = price / (1 + vatRate);
      return { base: Math.round(base * 100) / 100, vat: Math.round((price - base) * 100) / 100, total: price };
    }
    // exclusive
    const vat = price * vatRate;
    return { base: price, vat: Math.round(vat * 100) / 100, total: Math.round((price + vat) * 100) / 100 };
  };

  const calculateQuotationTotals = () => {
    const items = quotationForm.items;
    const regime = quotationForm.vat_regime || 'inclusive';
    const rate = effectiveVatConfig.default_rate || 16.0;
    
    let subtotal = 0;
    let discountTotal = 0;
    let baseTotal = 0;
    let vatTotal = 0;
    
    items.forEach(item => {
      const itemSubtotal = (item.quantity || 0) * (item.unit_price || 0);
      const itemDiscount = itemSubtotal * ((item.discount_percent || 0) / 100);
      const itemNet = itemSubtotal - itemDiscount;
      
      const vatCalc = calculateVAT(itemNet, rate, regime);
      
      subtotal += itemSubtotal;
      discountTotal += itemDiscount;
      baseTotal += vatCalc.base;
      vatTotal += vatCalc.vat;
    });
    
    const total = Math.round((baseTotal + vatTotal) * 100) / 100;
    
    return { 
      subtotal: Math.round(subtotal * 100) / 100, 
      discountTotal: Math.round(discountTotal * 100) / 100, 
      baseTotal: Math.round(baseTotal * 100) / 100,
      taxTotal: Math.round(vatTotal * 100) / 100,
      total 
    };
  };

  const handleSubmitQuotation = async (e) => {
    e.preventDefault();
    
    if (quotationForm.items.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }

    try {
      const totals = calculateQuotationTotals();
      const rate = effectiveVatConfig.default_rate || 16.0;
      const regime = quotationForm.vat_regime || 'inclusive';
      
      // Add VAT info to each item
      const itemsWithVat = quotationForm.items.map(item => {
        const itemSubtotal = (item.quantity || 0) * (item.unit_price || 0);
        const itemDiscount = itemSubtotal * ((item.discount_percent || 0) / 100);
        const itemNet = itemSubtotal - itemDiscount;
        const vatCalc = calculateVAT(itemNet, rate, regime);
        
        return {
          ...item,
          vat_regime: regime,
          vat_rate: rate,
          vat_amount: vatCalc.vat,
          base_price: vatCalc.base,
          tax_rate: rate,
          tax_amount: vatCalc.vat
        };
      });
      
      const data = {
        ...quotationForm,
        items: itemsWithVat,
        valid_until: quotationForm.valid_until ? new Date(quotationForm.valid_until).toISOString() : null,
        subtotal: totals.subtotal,
        discount_total: totals.discountTotal,
        base_total: totals.baseTotal,
        vat_total: totals.taxTotal,
        tax_total: totals.taxTotal,
        total: totals.total,
        vat_regime: regime
      };

      await documentsAPI.createQuotation(data);
      toast.success('Cotação criada com sucesso');
      setQuotationDialogOpen(false);
      resetQuotationForm();
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cotação');
    }
  };

  const handleApproveQuotation = async (quotation) => {
    try {
      const res = await documentsAPI.approveQuotation(quotation.id);
      const approvedAt = res.data?.approved_at;
      toast.success('Cotação aprovada', {
        description: approvedAt ? `Data/Hora aprovação: ${formatDateTime(approvedAt)}` : undefined
      });
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const toggleQuotationSelection = (quotationId, checked) => {
    setSelectedQuotationIds((prev) => {
      if (checked) {
        return prev.includes(quotationId) ? prev : [...prev, quotationId];
      }
      return prev.filter((id) => id !== quotationId);
    });
  };

  const toggleDocumentSelection = (setter, documentId, checked) => {
    setter((prev) => {
      if (checked) {
        return prev.includes(documentId) ? prev : [...prev, documentId];
      }
      return prev.filter((id) => id !== documentId);
    });
  };

  const toggleSelectAllDocumentGroup = (setter, entries, checked) => {
    if (checked) {
      setter(entries.map((entry) => entry.id));
      return;
    }
    setter([]);
  };

  const allQuotationsSelected = filteredQuotations.length > 0 && filteredQuotations.every((quotation) => selectedQuotationIds.includes(quotation.id));

  const toggleSelectAllQuotations = (checked) => {
    if (checked) {
      setSelectedQuotationIds(filteredQuotations.map((quotation) => quotation.id));
      return;
    }
    setSelectedQuotationIds([]);
  };

  const allReceptionGuidesSelected = filteredReceptionGuides.length > 0 && filteredReceptionGuides.every((guide) => selectedReceptionGuideIds.includes(guide.id));
  const allDeliveryGuidesSelected = filteredDeliveryGuides.length > 0 && filteredDeliveryGuides.every((guide) => selectedDeliveryGuideIds.includes(guide.id));
  const allServiceInvoicesSelected = filteredServiceInvoices.length > 0 && filteredServiceInvoices.every((invoice) => selectedServiceInvoiceIds.includes(invoice.id));
  const allCreditNotesSelected = filteredCreditNotes.length > 0 && filteredCreditNotes.every((creditNote) => selectedCreditNoteIds.includes(creditNote.id));

  const currentDocumentSelectionCount = {
    quotations: selectedQuotationIds.length,
    reception: selectedReceptionGuideIds.length,
    delivery: selectedDeliveryGuideIds.length,
    invoices: selectedServiceInvoiceIds.length,
    'credit-notes': selectedCreditNoteIds.length,
  }[activeTab] || 0;

  const handleBulkQuotationStatus = async (action) => {
    if (!selectedQuotationIds.length) {
      toast.error('Seleccione cotações para ação em massa');
      return;
    }
    try {
      const response = await documentsAPI.bulkQuotationStatus({
        ids: selectedQuotationIds,
        action,
        reason: action === 'deactivate' ? 'Cancelamento em massa via lista' : 'Restauro em massa via lista',
      });
      toast.success(response.data.message || 'Ação em massa concluída');
      setSelectedQuotationIds([]);
      await loadDocuments();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa de cotações'));
    }
  };

  const handleExportSelectedQuotations = () => {
    if (!selectedQuotationIds.length) {
      toast.error('Seleccione cotações para exportar');
      return;
    }
    const selected = filteredQuotations.filter((quotation) => selectedQuotationIds.includes(quotation.id));
    if (!selected.length) {
      toast.error('Nenhuma cotação seleccionada para exportação');
      return;
    }

    const headers = ['Número', 'Data/Hora', 'Funcionário', 'Cliente', 'Total', 'Estado'];
    const rows = selected.map((quotation) => [
      quotation.document_number || '',
      formatDateTime(quotation.created_at),
      quotation.created_by_name || '',
      quotation.customer_name || '',
      quotation.total || 0,
      quotation.status || '',
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `cotacoes_seleccionadas_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} cotações exportadas`);
  };

  const openConvertDialog = (quotation) => {
    const baseItems = (quotation.items || []).map((item) => ({
      product_id: item.product_id || '',
      description: item.description || item.product_name || '',
      sku: item.sku || '',
      quantity: Number(item.quantity || 1),
      unit_price: Number(item.unit_price || 0),
      discount_percent: Number(item.discount_percent || 0),
      discount_amount: Number(item.discount || item.discount_amount || 0),
      vat_regime: item.vat_regime || quotation.vat_regime || 'inclusive',
      vat_rate: Number(item.vat_rate || effectiveVatConfig.default_rate || 16),
    }));

    setQuotationToConvert(quotation);
    setConversionForm({
      document_type: 'vd',
      location_id: getPreferredStoreLocationId(quotation.location_id),
      items: baseItems
    });
    setConvertDialogOpen(true);
  };

  const handleConvertItemChange = (index, field, value) => {
    setConversionForm((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [field]: value };

      if (field === 'product_id') {
        const product = products.find((p) => p.id === value);
        if (product) {
          nextItems[index].description = product.name;
          nextItems[index].sku = product.sku || '';
          nextItems[index].unit_price = Number(product.unit_price || 0);
        }
      }

      return { ...prev, items: nextItems };
    });
  };

  const handleRemoveConvertItem = (index) => {
    setConversionForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const handleAddConvertItem = () => {
    setConversionForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: '',
          description: '',
          sku: '',
          quantity: 1,
          unit_price: 0,
          discount_percent: 0,
          discount_amount: 0,
          vat_regime: quotationToConvert?.vat_regime || 'inclusive',
          vat_rate: effectiveVatConfig.default_rate || 16,
        }
      ]
    }));
  };

  const conversionPreviewTotal = conversionForm.items.reduce((sum, item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unit_price || 0);
    const lineSubtotal = quantity * unitPrice;
    const discountByPercent = lineSubtotal * (Number(item.discount_percent || 0) / 100);
    const lineDiscount = Math.max(Number(item.discount_amount || 0), discountByPercent);
    return sum + Math.max(0, lineSubtotal - lineDiscount);
  }, 0);

  const handleConfirmConvertQuotation = async () => {
    if (!quotationToConvert?.id) return;
    if (!conversionForm.location_id) {
      toast.error('Seleccione a localização da venda convertida');
      return;
    }
    if (!conversionForm.items.length) {
      toast.error('Adicione pelo menos um item para converter');
      return;
    }

    setConvertingQuotation(true);
    try {
      const payload = {
        document_type: conversionForm.document_type,
        location_id: conversionForm.location_id,
        items: conversionForm.items
          .filter((item) => item.description && Number(item.quantity || 0) > 0)
          .map((item) => ({
            product_id: item.product_id || null,
            description: item.description,
            sku: item.sku || '',
            quantity: Number(item.quantity || 1),
            unit_price: Number(item.unit_price || 0),
            discount_percent: Number(item.discount_percent || 0),
            discount_amount: Number(item.discount_amount || 0),
            vat_regime: item.vat_regime || quotationToConvert?.vat_regime || 'inclusive',
            vat_rate: Number(item.vat_rate || effectiveVatConfig.default_rate || 16),
          }))
      };

      const res = await documentsAPI.convertToSale(quotationToConvert.id, payload);
      toast.success(`Venda criada: ${formatDisplayOnlyCode(res.data.sale_number, 'SAL')}`, {
        description: `Tipo documental: ${String(res.data.doc_type || '').toUpperCase()}`
      });

      setConvertDialogOpen(false);
      setQuotationToConvert(null);
      setConversionForm({ document_type: 'vd', location_id: '', items: [] });
      loadDocuments();

      if (res.data?.sale_id) {
        window.open(`/sales?sale=${res.data.sale_number || res.data.sale_id}&quote=${quotationToConvert.quotation_number || quotationToConvert.id}`, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao converter');
    } finally {
      setConvertingQuotation(false);
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setDetailDialogOpen(true);
  };

  const handleCreateNewClient = async (e) => {
    e.preventDefault();
    if (!newClientForm.name) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }
    
    setSavingClient(true);
    try {
      const res = await clientsAPI.create(newClientForm);
      const newClient = res.data;
      
      // Add to clients list
      setClients(prev => [...prev, newClient]);
      
      // Select the new client in the quotation form
      setQuotationForm(prev => ({
        ...prev,
        customer_id: newClient.id,
        customer_name: newClient.name,
        customer_phone: newClient.phone || '',
        customer_email: newClient.email || '',
        customer_nuit: newClient.nuit || '',
        customer_address: newClient.address || ''
      }));
      
      toast.success(`Cliente "${newClient.name}" criado e seleccionado`);
      setNewClientDialogOpen(false);
      setNewClientForm({
        name: '',
        phone: '',
        email: '',
        nuit: '',
        address: '',
        type: 'individual'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setSavingClient(false);
    }
  };

  const resetQuotationForm = () => {
    setQuotationForm({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_nuit: '',
      customer_address: '',
      valid_until: '',
      payment_condition_id: '',
      location_id: getPreferredStoreLocationId(),
      items: [],
      notes: '',
      terms: '',
      vat_regime: effectiveVatConfig.default_regime || 'inclusive'
    });
  };

  const totals = calculateQuotationTotals();

  const hasDocumentsData = Boolean(
    quotations.length ||
    receptionGuides.length ||
    deliveryGuides.length ||
    serviceInvoices.length ||
    products.length ||
    clients.length ||
    locations.length ||
    statistics
  );

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasDocumentsData}
      partialIssues={partialIssues}
      loadingTestId="documents-page-loading"
      partialIssuesTestId="documents-page-partial-issues"
      emptyState={
        <div className="p-6 space-y-6" data-testid="documents-page">
          <EmptyStatePanel
            title={t('documents.emptyTitle')}
            description={t('documents.emptyDescription')}
            testId="documents-page-empty-state"
          />
        </div>
      }
    >
    <div className="p-4 space-y-4 lg:p-5" data-testid="documents-page">
      <ModulePageHero
        testId="documents-page-hero"
        title={t('documents.title')}
        description={t('documents.description')}
        icon={FileText}
        badge={t('documents.badge')}
        stats={statistics ? [
          { label: t('documents.quotationsLabel'), value: statistics.quotations?.total || 0, helper: t('documents.quotationsPendingHelper', { count: statistics.quotations?.pending || 0 }) },
          { label: t('documents.conversionRateLabel'), value: `${statistics.quotations?.conversion_rate || 0}%`, helper: t('documents.conversionRateHelper') },
          { label: t('documents.receptionGuidesLabel'), value: statistics.reception_guides?.total || 0, helper: t('documents.receptionGuidesHelper') },
          { label: t('documents.deliveryGuidesLabel'), value: statistics.delivery_guides?.total || 0, helper: t('documents.deliveryGuidesHelper', { count: statistics.delivery_guides?.unsigned || 0 }) },
        ] : []}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1.5 bg-transparent p-0" data-testid="documents-tabs-list">
            <TabsTrigger value="quotations" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('documents.tabQuotations')}</TabsTrigger>
            <TabsTrigger value="reception" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('documents.tabReception')}</TabsTrigger>
            <TabsTrigger value="delivery" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('documents.tabDelivery')}</TabsTrigger>
            <TabsTrigger value="invoices" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('documents.tabInvoices')}</TabsTrigger>
            <TabsTrigger value="credit-notes" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('documents.tabCreditNotes')}</TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap items-center gap-1.5" data-testid="documents-context-controls-row">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('documents.searchPlaceholder')}
                className="h-9 w-56 pl-9 text-sm"
                data-testid="documents-context-search-input"
              />
            </div>

            <Select value={locationFilter} onValueChange={handleLocationFilterChange}>
              <SelectTrigger className="h-9 w-40 text-sm" data-testid="documents-context-location-select">
                <SelectValue placeholder={t('documents.locationPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('documents.allLocations')}</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(receivablesHref)}
              data-testid="documents-open-receivables-button"
              className="h-9 text-sm"
            >
              {t('documents.openReceivables')}
            </Button>

            {currentDocumentSelectionCount > 0 && (
              <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700" data-testid="documents-selection-badge">
                {currentDocumentSelectionCount} seleccionad{currentDocumentSelectionCount > 1 ? 'os' : 'o'}
              </Badge>
            )}

            {activeTab === 'quotations' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!selectedQuotationIds.length}
                  onClick={() => handleBulkQuotationStatus('deactivate')}
                  data-testid="documents-quotations-bulk-deactivate-button"
                >
                  {t('documents.bulkCancel', { count: selectedQuotationIds.length })}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!selectedQuotationIds.length}
                  onClick={() => handleBulkQuotationStatus('restore_soft')}
                  data-testid="documents-quotations-bulk-restore-button"
                >
                  {t('documents.bulkRestoreDraft')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!selectedQuotationIds.length}
                  onClick={handleExportSelectedQuotations}
                  data-testid="documents-quotations-bulk-export-button"
                >
                  {t('documents.exportSelected')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    resetQuotationForm();
                    setQuotationDialogOpen(true);
                  }}
                  data-testid="documents-new-quotation-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('documents.newQuotation')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Quotations Tab */}
        <TabsContent value="quotations" className="mt-4">
          <Card data-testid="quotations-list-card">
            <CardContent className="pt-4">
              {filteredQuotations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="quotations-empty-state">{t('documents.noQuotations')}</p>
              ) : (
                <div className="overflow-x-auto" data-testid="quotations-table-wrapper">
                  <Table className="text-sm" data-testid="quotations-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>
                          <Checkbox
                            checked={allQuotationsSelected}
                            onCheckedChange={(checked) => toggleSelectAllQuotations(Boolean(checked))}
                            data-testid="quotations-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.number')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.datetime')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.employee')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.client')}</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('documents.total')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.status')}</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('salesPage.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredQuotations.map((q) => {
                        const isSelected = selectedQuotationIds.includes(q.id);
                        return (
                          <TableRow key={q.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`quotation-row-${q.id}`}>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => toggleQuotationSelection(q.id, Boolean(checked))}
                                data-testid={`quotation-select-checkbox-${q.id}`}
                              />
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`quotation-number-${q.id}`}>
                              <div className="space-y-1">
                                <p className="font-mono text-sm font-semibold text-slate-900">{formatDisplayOnlyCode(q.document_number, 'COT')}</p>
                                <p className="text-[11px] text-slate-500">ID: {q.id?.slice?.(0, 8) || q.id}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`quotation-datetime-${q.id}`}>
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-900">{formatDateTime(q.created_at)}</p>
                                <p className="text-[11px] text-slate-500">{q.valid_until ? `Válida até ${formatDateTime(q.valid_until)}` : 'Sem validade definida'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`quotation-employee-${q.id}`}>
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-900">{q.created_by_name || 'N/A'}</p>
                                <p className="text-[11px] text-slate-500">{q.employee_number ? `Nº ${q.employee_number}` : 'Sem número interno'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`quotation-customer-${q.id}`}>
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-900">{q.customer_name || 'N/A'}</p>
                                <p className="text-[11px] text-slate-500">{q.customer_phone || q.customer_email || 'Sem contacto'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`} data-testid={`quotation-total-${q.id}`}>
                              <p className="font-medium text-slate-900">{formatCurrency(q.total)}</p>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`quotation-status-${q.id}`}>
                              <Badge className={statusColors[q.status] || 'bg-slate-100 text-slate-700'}>
                                {statusLabels[q.status] || (q.status || '').toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`}>
                              <UniversalCrudActions
                                entityLabel="quotation"
                                entityId={q.id}
                                canView
                                canEdit={false}
                                canDelete={false}
                                canRecover={false}
                                onView={() => handleViewDocument(q)}
                                extraActions={
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => downloadPdf('quotation', q, 'Original')} title="PDF Original" data-testid={`quotation-pdf-original-btn-${q.id}`}>
                                      <Download className="h-4 w-4 text-blue-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => downloadPdf('quotation', q, 'Duplicado')} title="PDF Duplicado" data-testid={`quotation-pdf-duplicate-btn-${q.id}`}>
                                      <Printer className="h-4 w-4 text-gray-600" />
                                    </Button>
                                    {(q.status === 'draft' || q.status === 'pending') && (
                                      <Button size="sm" variant="outline" className="h-8 rounded-lg border-green-200 text-green-700 hover:bg-green-50" onClick={() => handleApproveQuotation(q)} data-testid={`quotation-approve-btn-${q.id}`} title="Aprovar">
                                        <CheckCircle className="mr-1.5 h-4 w-4" />
                                        Aprovar
                                      </Button>
                                    )}
                                    {(q.status === 'approved' || q.status === 'accepted') && !q.is_expired && (
                                      <Button size="sm" variant="outline" className="h-8 rounded-lg border-purple-200 text-purple-700 hover:bg-purple-50" onClick={() => openConvertDialog(q)} data-testid={`quotation-convert-btn-${q.id}`} title="Converter para Venda">
                                        <ArrowRight className="mr-1.5 h-4 w-4" />
                                        Converter
                                      </Button>
                                    )}
                                  </>
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reception Guides Tab */}
        <TabsContent value="reception" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {filteredReceptionGuides.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('documents.noReceptionGuides')}</p>
              ) : (
                <div className="overflow-x-auto" data-testid="reception-guides-table-wrapper">
                  <Table className="text-sm" data-testid="reception-guides-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>
                          <Checkbox
                            checked={allReceptionGuidesSelected}
                            onCheckedChange={(checked) => toggleSelectAllDocumentGroup(setSelectedReceptionGuideIds, filteredReceptionGuides, Boolean(checked))}
                            data-testid="reception-guides-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Documento</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Serviço</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Cliente</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Equipamento</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('salesPage.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReceptionGuides.map((g) => {
                        const isSelected = selectedReceptionGuideIds.includes(g.id);
                        return (
                          <TableRow key={g.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`reception-guide-row-${g.id}`}>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => toggleDocumentSelection(setSelectedReceptionGuideIds, g.id, Boolean(checked))}
                                data-testid={`reception-guide-select-checkbox-${g.id}`}
                              />
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p className="font-medium text-slate-900">{formatDisplayOnlyCode(g.document_number, 'GR')}</p>
                                <p className="text-[11px] text-slate-500">{formatDateTime(g.created_at)}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Badge variant="outline" className="rounded-full border-slate-300 bg-white text-[11px] text-slate-700" data-testid={`reception-guide-service-number-${g.id}`}>
                                {formatDisplayOnlyCode(g.service_number, 'SRV')}
                              </Badge>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p className="font-medium text-slate-900">{g.customer_name}</p>
                                <p className="text-[11px] text-slate-500">{g.customer_phone || g.customer_email || 'Sem contacto'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-900">{[g.device_category, g.device_brand, g.device_model].filter(Boolean).join(' · ') || 'Equipamento não definido'}</p>
                                <p className="text-[11px] text-slate-500">IMEI/SN: {g.imei || g.serial_number || 'N/A'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`}>
                              <UniversalCrudActions
                                entityLabel="reception-guide"
                                entityId={g.id}
                                canView
                                canEdit={false}
                                canDelete={false}
                                canRecover={false}
                                onView={() => handleViewDocument(g)}
                                extraActions={
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => downloadPdf('reception', g, 'Original')} title="PDF Original" data-testid={`reception-pdf-original-${g.id}`}>
                                      <Download className="h-4 w-4 text-blue-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => downloadPdf('reception', g, 'Duplicado')} title="PDF Duplicado" data-testid={`reception-pdf-duplicate-${g.id}`}>
                                      <Printer className="h-4 w-4 text-gray-600" />
                                    </Button>
                                  </>
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delivery Guides Tab */}
        <TabsContent value="delivery" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {filteredDeliveryGuides.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('documents.noDeliveryGuides')}</p>
              ) : (
                <div className="overflow-x-auto" data-testid="delivery-guides-table-wrapper">
                  <Table className="text-sm" data-testid="delivery-guides-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>
                          <Checkbox
                            checked={allDeliveryGuidesSelected}
                            onCheckedChange={(checked) => toggleSelectAllDocumentGroup(setSelectedDeliveryGuideIds, filteredDeliveryGuides, Boolean(checked))}
                            data-testid="delivery-guides-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Documento</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Cliente</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Itens</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>Recepção</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>Total</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('salesPage.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDeliveryGuides.map((g) => {
                        const isSelected = selectedDeliveryGuideIds.includes(g.id);
                        return (
                          <TableRow key={g.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`delivery-guide-row-${g.id}`}>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Checkbox
                                checked={Boolean(isSelected)}
                                onCheckedChange={(checked) => toggleDocumentSelection(setSelectedDeliveryGuideIds, g.id, Boolean(checked))}
                                data-testid={`delivery-guide-select-checkbox-${g.id}`}
                              />
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p className="font-medium text-slate-900">{formatDisplayOnlyCode(g.document_number, 'GE')}</p>
                                <p className="text-[11px] text-slate-500">{formatDateTime(g.created_at)}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p className="font-medium text-slate-900">{g.customer_name}</p>
                                <p className="text-[11px] text-slate-500">{g.customer_phone || g.customer_email || 'Sem contacto'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-900">{g.item_count} itens</p>
                                <p className="text-[11px] text-slate-500">{g.service_number ? formatDisplayOnlyCode(g.service_number, 'SRV') : 'Sem serviço associado'}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Badge variant={g.receiver_signature ? 'default' : 'secondary'} data-testid={`delivery-guide-status-${g.id}`}>
                                {g.receiver_signature ? t('documents.signed') : t('documents.pending')}
                              </Badge>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`}>
                              <p className="font-semibold text-slate-900">{formatCurrency(g.total_value)}</p>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`}>
                              <UniversalCrudActions
                                entityLabel="delivery-guide"
                                entityId={g.id}
                                canView
                                canEdit={false}
                                canDelete={false}
                                canRecover={false}
                                onView={() => handleViewDocument(g)}
                                extraActions={
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => downloadPdf('delivery', g, 'Original')} title="PDF Original" data-testid={`delivery-pdf-original-${g.id}`}>
                                      <Download className="h-4 w-4 text-blue-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => downloadPdf('delivery', g, 'Duplicado')} title="PDF Duplicado" data-testid={`delivery-pdf-duplicate-${g.id}`}>
                                      <Printer className="h-4 w-4 text-gray-600" />
                                    </Button>
                                  </>
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card data-testid="service-invoices-list-card">
            <CardContent className="pt-4">
              {filteredServiceInvoices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="service-invoices-empty-state">{t('documents.noServiceInvoices')}</p>
              ) : (
                <div className="overflow-x-auto" data-testid="service-invoices-table-wrapper">
                  <Table className="text-sm" data-testid="service-invoices-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>
                          <Checkbox
                            checked={allServiceInvoicesSelected}
                            onCheckedChange={(checked) => toggleSelectAllDocumentGroup(setSelectedServiceInvoiceIds, filteredServiceInvoices, Boolean(checked))}
                            data-testid="service-invoices-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.invoice')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.service')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.client')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.items')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.description')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('labels.date')}</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('documents.total')}</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('salesPage.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredServiceInvoices.map((invoice) => {
                        const isSelected = selectedServiceInvoiceIds.includes(invoice.id);
                        return (
                          <TableRow key={invoice.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`service-invoice-row-${invoice.id}`}>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => toggleDocumentSelection(setSelectedServiceInvoiceIds, invoice.id, Boolean(checked))}
                                data-testid={`service-invoice-select-checkbox-${invoice.id}`}
                              />
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} font-mono text-sm`} data-testid={`service-invoice-number-${invoice.id}`}>
                              {formatDisplayOnlyCode(invoice.invoice_number, 'INV')}
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`service-invoice-service-${invoice.id}`}>
                              {formatDisplayOnlyCode(invoice.service_number, 'SRV')}
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`service-invoice-customer-${invoice.id}`}>{invoice.customer_name || 'N/A'}</TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`service-invoice-items-count-${invoice.id}`}>
                              {(invoice.items || []).length}
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} max-w-[280px]`} data-testid={`service-invoice-items-summary-${invoice.id}`}>
                              <p className="truncate">
                                {(invoice.items || []).map((item) => item.product_name || item.description || 'Artigo').slice(0, 2).join(' • ') || 'N/A'}
                              </p>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`service-invoice-date-${invoice.id}`}>{formatDateTime(invoice.created_at)}</TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right font-medium`} data-testid={`service-invoice-total-${invoice.id}`}>{formatCurrency(invoice.total || 0)}</TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`}>
                              <UniversalCrudActions
                                entityLabel="service-invoice"
                                entityId={invoice.id}
                                canView={false}
                                canEdit={false}
                                canDelete={false}
                                canRecover={false}
                                extraActions={
                                  <>
                                    <Button size="sm" variant="outline" className="h-8 rounded-lg border-purple-200 text-purple-700 hover:bg-purple-50" onClick={() => openCreditNoteDialog('service_invoice', invoice)} title="Emitir Nota de Crédito" data-testid={`service-invoice-credit-note-${invoice.id}`}>
                                      <RefreshCcw className="mr-1.5 h-4 w-4" />
                                      Nota crédito
                                    </Button>
                                    <div className="flex items-center gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => downloadPdf('service_invoice', invoice, 'Original')} title="Descarregar Original" data-testid={`service-invoice-download-original-${invoice.id}`}>
                                        <Download className="h-4 w-4 text-blue-600" />
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => downloadPdf('service_invoice', invoice, 'Duplicado')} title="Descarregar Duplicado" data-testid={`service-invoice-download-duplicate-${invoice.id}`}>
                                        Duplicado
                                      </Button>
                                    </div>
                                  </>
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credit-notes" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="flex items-center gap-2"><RefreshCcw className="h-5 w-5 text-purple-600" /> {t('documents.creditNotes.title')}</CardTitle>
                  <CardDescription>{t('documents.creditNotes.description')}</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={loadDocuments} data-testid="documents-credit-note-refresh-button">
                    <RefreshCcw className="h-4 w-4 mr-2" /> {t('documents.creditNotes.refreshButton')}
                  </Button>
                  <Button onClick={() => openCreditNoteDialog('sale')} data-testid="documents-new-credit-note-button">
                    <Plus className="h-4 w-4 mr-2" /> {t('documents.creditNotes.newButton')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-5 space-y-4" data-testid="credit-note-filter-panel">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="credit-note-cockpit-grid">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="credit-note-summary-total">
                    <p className="text-xs uppercase tracking-wide text-slate-500">{t('documents.creditNotes.summary.total', { count: creditNoteSummary.total })}</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{t('documents.creditNotes.visibleResults', { count: creditNoteOperationalSummary.visible })}</p>
                  </div>
                  <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4" data-testid="credit-note-summary-pending">
                    <p className="text-xs uppercase tracking-wide text-amber-700">{t('documents.creditNotes.summary.pending', { count: creditNoteSummary.pending })}</p>
                    <p className="mt-2 text-lg font-semibold text-amber-900">{t('documents.creditNotes.summary.pendingActions', { count: creditNoteOperationalSummary.pendingActions })}</p>
                  </div>
                  <div className="rounded-xl border border-purple-200 bg-purple-50/70 p-4" data-testid="credit-note-summary-amount">
                    <p className="text-xs uppercase tracking-wide text-purple-700">{t('documents.creditNotes.summary.totalAmount', { amount: formatCurrency(creditNoteOperationalSummary.totalAmount) })}</p>
                    <p className="mt-2 text-lg font-semibold text-purple-900">{t('documents.creditNotes.refundMethods.credit')}</p>
                    <p className="text-sm text-purple-700">{creditNoteOperationalSummary.customerCredit}</p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4" data-testid="credit-note-summary-processed">
                    <p className="text-xs uppercase tracking-wide text-emerald-700">{t('documents.creditNotes.summary.processed', { count: creditNoteSummary.processed })}</p>
                    <p className="mt-2 text-lg font-semibold text-emerald-900">{t('documents.creditNotes.summary.approved', { count: creditNoteSummary.approved })}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2" data-testid="credit-note-quick-filters">
                  <Button variant={creditNoteStatusFilter === 'pending' ? 'default' : 'outline'} size="sm" onClick={() => applyCreditNoteQuickFilter('pending')} data-testid="credit-note-quick-filter-pending">{t('documents.creditNotes.quickFilters.pending')}</Button>
                  <Button variant={creditNoteStatusFilter === 'approved' ? 'default' : 'outline'} size="sm" onClick={() => applyCreditNoteQuickFilter('approved')} data-testid="credit-note-quick-filter-approved">{t('documents.creditNotes.quickFilters.approved')}</Button>
                  <Button variant={creditNoteRefundFilter === 'credit' ? 'default' : 'outline'} size="sm" onClick={() => applyCreditNoteQuickFilter('customerCredit')} data-testid="credit-note-quick-filter-credit">{t('documents.creditNotes.quickFilters.customerCredit')}</Button>
                  <Button variant={creditNoteSourceFilter === 'sale' ? 'default' : 'outline'} size="sm" onClick={() => applyCreditNoteQuickFilter('saleOrigin')} data-testid="credit-note-quick-filter-sale">{t('documents.creditNotes.quickFilters.saleOrigin')}</Button>
                  <Button variant={creditNoteSourceFilter === 'service_invoice' ? 'default' : 'outline'} size="sm" onClick={() => applyCreditNoteQuickFilter('serviceOrigin')} data-testid="credit-note-quick-filter-service">{t('documents.creditNotes.quickFilters.serviceOrigin')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => applyCreditNoteQuickFilter('clear')} data-testid="credit-note-clear-filters-button">{t('documents.creditNotes.quickFilters.clear')}</Button>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="space-y-1">
                    <Label htmlFor="credit-note-status-filter">{t('documents.creditNotes.filters.status')}</Label>
                    <Select value={creditNoteStatusFilter} onValueChange={setCreditNoteStatusFilter}>
                      <SelectTrigger id="credit-note-status-filter" data-testid="credit-note-status-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('documents.creditNotes.filters.allStatuses')}</SelectItem>
                        <SelectItem value="pending">{t('documents.creditNotes.statuses.pending')}</SelectItem>
                        <SelectItem value="approved">{t('documents.creditNotes.statuses.approved')}</SelectItem>
                        <SelectItem value="processed">{t('documents.creditNotes.statuses.processed')}</SelectItem>
                        <SelectItem value="rejected">{t('documents.creditNotes.statuses.rejected')}</SelectItem>
                        <SelectItem value="cancelled">{t('documents.creditNotes.statuses.cancelled')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="credit-note-refund-filter">{t('documents.creditNotes.filters.refundMethod')}</Label>
                    <Select value={creditNoteRefundFilter} onValueChange={setCreditNoteRefundFilter}>
                      <SelectTrigger id="credit-note-refund-filter" data-testid="credit-note-refund-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('documents.creditNotes.filters.allRefundMethods')}</SelectItem>
                        <SelectItem value="credit">{t('documents.creditNotes.refundMethods.credit')}</SelectItem>
                        <SelectItem value="cash">{t('documents.creditNotes.refundMethods.cash')}</SelectItem>
                        <SelectItem value="bank_transfer">{t('documents.creditNotes.refundMethods.bank_transfer')}</SelectItem>
                        <SelectItem value="replacement">{t('documents.creditNotes.refundMethods.replacement')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="credit-note-source-filter">{t('documents.creditNotes.filters.origin')}</Label>
                    <Select value={creditNoteSourceFilter} onValueChange={setCreditNoteSourceFilter}>
                      <SelectTrigger id="credit-note-source-filter" data-testid="credit-note-source-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('documents.creditNotes.filters.allOrigins')}</SelectItem>
                        <SelectItem value="sale">{t('documents.creditNotes.sourceTypes.sale')}</SelectItem>
                        <SelectItem value="service_invoice">{t('documents.creditNotes.sourceTypes.service_invoice')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="credit-note-location-filter">{t('documents.creditNotes.filters.location')}</Label>
                    <Select value={locationFilter || 'all'} onValueChange={handleLocationFilterChange}>
                      <SelectTrigger id="credit-note-location-filter" data-testid="credit-note-location-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('documents.allLocations')}</SelectItem>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center" data-testid="credit-note-active-filters-row">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('documents.creditNotes.activeFiltersLabel')}</span>
                  {activeCreditNoteFilters.length > 0 ? activeCreditNoteFilters.map((filterLabel) => (
                    <Badge key={filterLabel} variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">{filterLabel}</Badge>
                  )) : (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t('documents.creditNotes.filters.clear')}</Badge>
                  )}
                  <Badge variant="outline" className="bg-white text-slate-700 border-slate-200" data-testid="credit-note-visible-results-badge">
                    {t('documents.creditNotes.visibleResults', { count: creditNoteOperationalSummary.visible })}
                  </Badge>
                </div>
              </div>

              {filteredCreditNotes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-10 text-center" data-testid="credit-notes-empty-state-panel">
                  <p className="text-muted-foreground">
                    {creditNotes.length > 0 ? t('documents.creditNotes.filteredEmpty') : t('documents.creditNotes.empty')}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">{t('documents.creditNotes.emptyHint')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="text-sm" data-testid="credit-notes-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>
                          <Checkbox
                            checked={allCreditNotesSelected}
                            onCheckedChange={(checked) => toggleSelectAllDocumentGroup(setSelectedCreditNoteIds, filteredCreditNotes, Boolean(checked))}
                            data-testid="credit-notes-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.number')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.originalDocument')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.origin')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.customer')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.reason')}</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('documents.creditNotes.columns.amount')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.refund')}</TableHead>
                        <TableHead className={INVENTORY_TABLE_HEAD_CLASS}>{t('documents.creditNotes.columns.status')}</TableHead>
                        <TableHead className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>{t('documents.creditNotes.columns.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCreditNotes.map((nc) => {
                        const isSelected = selectedCreditNoteIds.includes(nc.id);
                        return (
                          <TableRow key={nc.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`credit-note-row-${nc.id}`}>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => toggleDocumentSelection(setSelectedCreditNoteIds, nc.id, Boolean(checked))}
                                data-testid={`credit-note-select-checkbox-${nc.id}`}
                              />
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} font-medium`} data-testid={`credit-note-number-${nc.id}`}>{formatDisplayOnlyCode(nc.nc_number, 'NC')}</TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <div className="space-y-1">
                                <p>{formatDisplayOnlyCode(nc.original_document_number, 'FAC')}</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200" data-testid={`credit-note-location-badge-${nc.id}`}>
                                    {creditNoteLocationNameById.get(nc.location_id) || t('documents.creditNotes.filters.location')}
                                  </Badge>
                                  <span className="text-muted-foreground">{formatDateTime(nc.created_at)}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS}>
                              <Badge
                                variant="outline"
                                className={creditNoteSourceMeta[nc.source_type]?.className || 'bg-slate-100 text-slate-700 border-slate-200'}
                                data-testid={`credit-note-origin-badge-${nc.id}`}
                              >
                                {creditNoteSourceMeta[nc.source_type]?.label || nc.source_type || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-sm`}>{nc.customer_name || 'N/A'}</TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} max-w-[220px] text-sm truncate`} title={nc.reason}>{nc.reason}</TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right font-semibold text-purple-700`}>{formatCurrency(nc.total_amount || 0)}</TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-sm`}>
                              <Badge
                                variant="outline"
                                className={creditNoteRefundMeta[nc.refund_method]?.className || 'bg-slate-100 text-slate-700 border-slate-200'}
                                data-testid={`credit-note-refund-badge-${nc.id}`}
                              >
                                {creditNoteRefundMeta[nc.refund_method]?.label || nc.refund_method || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-sm`}>
                              <Badge
                                variant="outline"
                                className={creditNoteStatusMeta[nc.status]?.className || 'bg-slate-100 text-slate-700 border-slate-200'}
                                data-testid={`credit-note-status-badge-${nc.id}`}
                              >
                                {creditNoteStatusMeta[nc.status]?.label || nc.status || '—'}
                              </Badge>
                              <p className="mt-1 text-xs text-muted-foreground">{getCreditNoteNextStepLabel(nc.status)}</p>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`}>
                              <div className="flex justify-end gap-1">
                                {nc.status === 'pending' && <Button size="sm" variant="outline" className="h-8 rounded-lg border-green-200 text-green-700 hover:bg-green-50" onClick={() => handleApproveCreditNote(nc.id)} title={t('documents.creditNotes.actions.approve')} data-testid={`credit-note-approve-${nc.id}`}><CheckCircle className="mr-1.5 h-4 w-4" />Aprovar</Button>}
                                {nc.status === 'approved' && <Button size="sm" variant="outline" className="h-8 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => handleProcessCreditNote(nc.id)} title={t('documents.creditNotes.actions.process')} data-testid={`credit-note-process-${nc.id}`}><ArrowRight className="mr-1.5 h-4 w-4" />Processar</Button>}
                                <Button size="sm" variant="ghost" onClick={() => downloadCreditNotePDF(nc.id, 'Original')} title={`${t('documents.creditNotes.actions.pdf')} Original`} data-testid={`credit-note-pdf-original-${nc.id}`}><Download className="h-4 w-4 text-blue-600" /></Button>
                                <Button size="sm" variant="outline" onClick={() => downloadCreditNotePDF(nc.id, 'Duplicado')} title="PDF Duplicado" data-testid={`credit-note-pdf-duplicate-${nc.id}`}>Duplicado</Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Quotation Dialog */}
      <Dialog open={quotationDialogOpen} onOpenChange={setQuotationDialogOpen}>
        <DialogContent className="w-[min(96vw,1180px)] max-w-[1180px] max-h-[88vh] overflow-y-auto p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle>{t('documents.newQuotation')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitQuotation} className="space-y-6">
            {/* Customer */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">{t('labels.customer')}</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setNewClientDialogOpen(true)}
                  data-testid="new-client-btn"
                >
                  <UserPlus className="h-3 w-3 mr-1" /> {t('documents.newClient')}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('documents.selectClient')}</Label>
                  <Select value={quotationForm.customer_id} onValueChange={handleClientSelect}>
                    <SelectTrigger data-testid="select-client-trigger">
                      <SelectValue placeholder={t('documents.selectPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.nuit ? `(NUIT: ${c.nuit})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('labels.name')} *</Label>
                  <Input
                    value={quotationForm.customer_name}
                    onChange={(e) => setQuotationForm({...quotationForm, customer_name: e.target.value})}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label>NUIT</Label>
                  <Input
                    value={quotationForm.customer_nuit}
                    onChange={(e) => setQuotationForm({...quotationForm, customer_nuit: e.target.value})}
                    placeholder="Ex: 400123456"
                    data-testid="customer-nuit-input"
                  />
                </div>
                <div>
                  <Label>{t('labels.phone')}</Label>
                  <Input
                    value={quotationForm.customer_phone}
                    onChange={(e) => setQuotationForm({...quotationForm, customer_phone: e.target.value})}
                    data-testid="customer-phone-input"
                  />
                </div>
                <div>
                  <Label>{t('labels.email')}</Label>
                  <Input
                    type="email"
                    value={quotationForm.customer_email}
                    onChange={(e) => setQuotationForm({...quotationForm, customer_email: e.target.value})}
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label>{t('labels.address')}</Label>
                  <Input
                    value={quotationForm.customer_address}
                    onChange={(e) => setQuotationForm({...quotationForm, customer_address: e.target.value})}
                    placeholder={t('labels.address')}
                    data-testid="customer-address-input"
                  />
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">{t('documents.itemsAvailable', { count: products.length })}</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem} data-testid="add-item-btn">
                  <Plus className="h-3 w-3 mr-1" /> {t('documents.addItem')}
                </Button>
              </div>

              {quotationForm.items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('documents.addItemsHint')}
                </p>
              ) : (
                <div className="space-y-2">
                  {quotationForm.items.map((item, idx) => {
                    // Find the selected product to show specs
                    const selectedProduct = products.find(p => p.id === item.product_id);
                    const specs = selectedProduct?.specs || {};
                    const specsSummary = [];
                    
                    // Extract basic specs
                    if (specs.cpu?.modelo) specsSummary.push(`CPU: ${specs.cpu.modelo}`);
                    if (specs.memoria?.capacidade) specsSummary.push(`RAM: ${specs.memoria.capacidade}`);
                    if (specs.armazenamento?.[0]) {
                      const storage = specs.armazenamento[0];
                      specsSummary.push(`${storage.tipo || 'Disco'}: ${storage.capacidade}`);
                    }
                    if (specs.ecra?.polegadas) specsSummary.push(`Ecrã: ${specs.ecra.polegadas}`);
                    if (specs.grafica?.gpu1) specsSummary.push(`GPU: ${specs.grafica.gpu1}`);
                    
                    return (
                    <div key={getDocumentItemKey(item, idx, 'quotation-form-item')} className="p-3 bg-slate-50 rounded-lg space-y-2">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-4">
                          <ProductCombobox
                            products={products}
                            value={item.product_id}
                            onValueChange={(v) => handleItemChange(idx, 'product_id', v)}
                            placeholder="Seleccionar produto..."
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                            placeholder="Qtd"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                            placeholder="Preço"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={item.discount_percent}
                            onChange={(e) => handleItemChange(idx, 'discount_percent', e.target.value)}
                            placeholder="Desc %"
                          />
                        </div>
                        <div className="col-span-1 text-right font-medium">
                          {formatCurrency(item.total || 0)}
                        </div>
                        <div className="col-span-1 text-right">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveItem(idx)}>
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      {/* Show product specs if available */}
                      {specsSummary.length > 0 && (
                        <div className="text-xs text-blue-700 bg-blue-50 px-3 py-1.5 rounded border border-blue-200">
                          <span className="font-medium">Specs:</span> {specsSummary.join(' • ')}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Totals with VAT */}
              {quotationForm.items.length > 0 && (
                <div className="bg-slate-100 p-4 rounded-lg space-y-4">
                  {/* VAT Regime Selector */}
                  <div className="flex items-center gap-4 pb-3 border-b border-slate-200">
                    <Label className="font-medium text-slate-700">{t('documents.vatRegime')}</Label>
                    <Select 
                      value={quotationForm.vat_regime} 
                      onValueChange={(v) => setQuotationForm({...quotationForm, vat_regime: v})}
                    >
                      <SelectTrigger className="w-[180px] bg-white" data-testid="quotation-vat-regime">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inclusive">{t('documents.vatIncluded')}</SelectItem>
                        <SelectItem value="exclusive">{t('documents.vatExcluded')}</SelectItem>
                        <SelectItem value="exempt">{t('documents.exempt')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Badge variant="outline" className="text-xs">
                      {t('documents.rate')}: {effectiveVatConfig.default_rate}%
                    </Badge>
                  </div>
                  
                  {/* Totals breakdown */}
                  <div className="flex justify-end gap-8">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('documents.subtotal')}</p>
                      <p className="font-medium">{formatCurrency(totals.subtotal)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('documents.discounts')}</p>
                      <p className="font-medium text-red-600">-{formatCurrency(totals.discountTotal)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('documents.taxableBase')}</p>
                      <p className="font-medium">{formatCurrency(totals.baseTotal)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('documents.vat')} ({effectiveVatConfig.default_rate}%)</p>
                      <p className="font-medium text-blue-600">{formatCurrency(totals.taxTotal)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('documents.total')}</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(totals.total)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('documents.validity')}</Label>
                <Input
                  type="date"
                  value={quotationForm.valid_until}
                  onChange={(e) => setQuotationForm({...quotationForm, valid_until: e.target.value})}
                />
              </div>
              <div>
                <Label>{t('documents.location')}</Label>
                <Select 
                  value={quotationForm.location_id} 
                  onValueChange={(v) => setQuotationForm({...quotationForm, location_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('documents.selectPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.filter(l => l.type === 'store').map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>{t('labels.notes')}</Label>
              <Textarea
                value={quotationForm.notes}
                onChange={(e) => setQuotationForm({...quotationForm, notes: e.target.value})}
                placeholder={t('documents.additionalNotesPlaceholder')}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuotationDialogOpen(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit">
                {t('documents.createQuotation')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert Quotation Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="w-[min(96vw,1220px)] max-w-[1220px] max-h-[88vh] overflow-y-auto p-4 sm:p-5" data-testid="quotation-convert-dialog">
          <DialogHeader>
            <DialogTitle>{t('documents.convertQuotationTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-slate-50 rounded-lg border">
              <div>
                <Label>{t('documents.documentType')}</Label>
                <Select
                  value={conversionForm.document_type}
                  onValueChange={(value) => setConversionForm((prev) => ({ ...prev, document_type: value }))}
                >
                  <SelectTrigger data-testid="quotation-convert-doc-type-select">
                    <SelectValue placeholder={t('documents.selectDocumentType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {quotationDocTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('documents.location')}</Label>
                <Select
                  value={conversionForm.location_id}
                  onValueChange={(value) => setConversionForm((prev) => ({ ...prev, location_id: value }))}
                >
                  <SelectTrigger data-testid="quotation-convert-location-select">
                    <SelectValue placeholder={t('documents.selectLocation')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end justify-start md:justify-end">
                <Button type="button" variant="outline" onClick={handleAddConvertItem} data-testid="quotation-convert-add-item-button">
                  <Plus className="h-3 w-3 mr-1" /> {t('documents.addItem')}
                </Button>
              </div>
            </div>

            <div className="space-y-2" data-testid="quotation-convert-items-list">
              {conversionForm.items.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="quotation-convert-items-empty">{t('documents.noItemsToConvert')}</p>
              ) : (
                conversionForm.items.map((item, index) => (
                  <div key={getDocumentItemKey(item, index, 'quotation-convert-item')} className="grid grid-cols-12 gap-2 p-3 border rounded-lg bg-white" data-testid={`quotation-convert-item-row-${index}`}>
                    <div className="col-span-12 md:col-span-4">
                      <Label className="text-xs">{t('documents.productDescription')}</Label>
                      <ProductCombobox
                        products={products}
                        value={item.product_id || ''}
                        onValueChange={(value) => handleConvertItemChange(index, 'product_id', value)}
                        placeholder={t('documents.selectProduct')}
                      />
                      <Input
                        className="mt-2"
                        value={item.description || ''}
                        onChange={(e) => handleConvertItemChange(index, 'description', e.target.value)}
                        placeholder={t('documents.itemDescriptionPlaceholder')}
                        data-testid={`quotation-convert-item-description-${index}`}
                      />
                    </div>

                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Qtd</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleConvertItemChange(index, 'quantity', Number(e.target.value || 1))}
                        data-testid={`quotation-convert-item-qty-${index}`}
                      />
                    </div>

                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">{t('documents.price')}</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleConvertItemChange(index, 'unit_price', Number(e.target.value || 0))}
                        data-testid={`quotation-convert-item-price-${index}`}
                      />
                    </div>

                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Desc %</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.discount_percent || 0}
                        onChange={(e) => handleConvertItemChange(index, 'discount_percent', Number(e.target.value || 0))}
                        data-testid={`quotation-convert-item-discount-${index}`}
                      />
                    </div>

                    <div className="col-span-6 md:col-span-2 flex items-end justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleRemoveConvertItem(index)}
                        data-testid={`quotation-convert-item-remove-${index}`}
                      >
                        <XCircle className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-between items-center p-3 rounded-lg bg-slate-100 border" data-testid="quotation-convert-total-preview">
              <span className="text-sm text-muted-foreground">{t('documents.totalPreview')}</span>
              <span className="text-lg font-bold text-emerald-700">{formatCurrency(conversionPreviewTotal)}</span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConvertDialogOpen(false)} data-testid="quotation-convert-cancel-button">
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmConvertQuotation}
              disabled={convertingQuotation}
              data-testid="quotation-convert-confirm-button"
            >
              {convertingQuotation ? 'A converter...' : 'Converter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={creditNoteDialogOpen} onOpenChange={setCreditNoteDialogOpen}>
        <DialogContent className="w-[min(96vw,1080px)] max-w-[1080px] max-h-[88vh] overflow-y-auto p-4 sm:p-5" data-testid="credit-note-dialog">
          <DialogHeader>
            <DialogTitle>Emitir Nota de Crédito</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg border">
              <div>
                <Label>Origem</Label>
                <Select value={creditNoteForm.source_type} onValueChange={(value) => setCreditNoteForm((prev) => ({ ...prev, source_type: value, source_id: '', items: [] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Venda</SelectItem>
                    <SelectItem value="service_invoice">Factura de Serviço</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Documento original</Label>
                <Select value={creditNoteForm.source_id} onValueChange={handleCreditNoteSourceChange}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar documento" /></SelectTrigger>
                  <SelectContent>
                    {creditNoteSourceOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Tipo de Nota</Label>
                <Select value={creditNoteForm.note_type} onValueChange={(value) => setCreditNoteForm((prev) => ({ ...prev, note_type: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_cancellation">Anulação total</SelectItem>
                    <SelectItem value="partial_return">Devolução parcial</SelectItem>
                    <SelectItem value="full_return">Devolução total</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Método de Reembolso</Label>
                <Select value={creditNoteForm.refund_method} onValueChange={(value) => setCreditNoteForm((prev) => ({ ...prev, refund_method: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Numerário</SelectItem>
                    <SelectItem value="credit">Crédito</SelectItem>
                    <SelectItem value="store_credit">Crédito Loja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={creditNoteForm.restock_items} onCheckedChange={(checked) => setCreditNoteForm((prev) => ({ ...prev, restock_items: Boolean(checked) }))} />
                  Actualizar stock na devolução
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Textarea value={creditNoteForm.reason} onChange={(e) => setCreditNoteForm((prev) => ({ ...prev, reason: e.target.value }))} rows={2} placeholder="Explique a anulação ou devolução..." />
            </div>

            <div className="space-y-2" data-testid="credit-note-items-list">
              {creditNoteForm.items.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem artigos para creditar</p>
              ) : (
                creditNoteForm.items.map((item, index) => (
                  <div key={getDocumentItemKey(item, index, 'credit-note-item')} className="grid grid-cols-12 gap-2 p-3 border rounded-lg bg-white">
                    <div className="col-span-12 md:col-span-5">
                      <Label className="text-xs">Artigo</Label>
                      <div className="h-10 flex items-center rounded-md border px-3 text-sm bg-slate-50">{item.product_name || 'Artigo'}</div>
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs">Qtd</Label>
                      <Input type="number" min="0" step="1" value={item.quantity} onChange={(e) => handleCreditNoteItemChange(index, 'quantity', Number(e.target.value || 0))} />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs">Preço</Label>
                      <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => handleCreditNoteItemChange(index, 'unit_price', Number(e.target.value || 0))} />
                    </div>
                    <div className="col-span-4 md:col-span-3 flex items-end justify-end">
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox checked={item.selected} onCheckedChange={(checked) => handleCreditNoteItemChange(index, 'selected', Boolean(checked))} />
                        Incluir
                      </label>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-2">
              <Label>Notas adicionais</Label>
              <Textarea value={creditNoteForm.notes} onChange={(e) => setCreditNoteForm((prev) => ({ ...prev, notes: e.target.value }))} rows={2} placeholder="Notas internas da nota de crédito..." />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreditNoteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleCreateCreditNote} disabled={creatingCreditNote} data-testid="credit-note-create-button">
                {creatingCreditNote ? 'A emitir...' : 'Emitir Nota de Crédito'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Documento</DialogTitle>
          </DialogHeader>

          {selectedDocument && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold" data-testid="document-detail-number">{getDocumentDisplayNumber(selectedDocument)}</h3>
                  <p className="text-sm text-muted-foreground">{selectedDocument.customer_name}</p>
                </div>
                <Badge className={statusColors[selectedDocument.status]}>
                  {statusLabels[selectedDocument.status]}
                </Badge>
              </div>

              {selectedDocument.document_type === 'quotation' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border" data-testid="quotation-detail-summary">
                  <div>
                    <p className="text-xs text-muted-foreground">Aprovada em</p>
                    <p className="font-medium" data-testid="quotation-detail-approved-at">{selectedDocument.approved_at ? formatDateTime(selectedDocument.approved_at) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Aprovada por</p>
                    <p className="font-medium" data-testid="quotation-detail-approved-by">{selectedDocument.approved_by_name || '—'}</p>
                  </div>
                  <div className="md:col-span-2">
                    {(selectedDocument.converted_sale_id || selectedDocument.converted_to_sale_id) ? (
                      <div className="flex flex-wrap items-center gap-2" data-testid="quotation-detail-converted-link-block">
                        <span className="text-sm text-muted-foreground">Convertida para venda:</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/sales?sale=${selectedDocument.converted_sale_number || selectedDocument.converted_to_sale_number || selectedDocument.converted_sale_id || selectedDocument.converted_to_sale_id}&quote=${selectedDocument.quotation_number || selectedDocument.id}`, '_blank')}
                          data-testid="quotation-detail-open-converted-sale-link"
                        >
                          {formatDisplayOnlyCode(selectedDocument.converted_sale_number || selectedDocument.converted_to_sale_number || selectedDocument.converted_sale_id || selectedDocument.converted_to_sale_id, 'SAL')}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid="quotation-detail-converted-empty">Ainda não convertida para venda</p>
                    )}
                  </div>
                </div>
              )}

              {Array.isArray(selectedDocument.items) && (
                <div>
                  <h4 className="font-semibold mb-2">Itens</h4>
                  <div className="space-y-2">
                    {selectedDocument.items.map((item, idx) => (
                      <div key={getDocumentItemKey(item, idx, 'document-detail-item')} className="flex justify-between p-2 bg-slate-50 rounded">
                        <div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} x {formatCurrency(item.unit_price)}
                          </p>
                        </div>
                        <p className="font-medium">{formatCurrency(item.total)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedDocument.total)}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedDocument.reported_issue && (
                <div>
                  <h4 className="font-semibold mb-2">Problema Reportado</h4>
                  <p className="text-sm bg-slate-50 p-3 rounded">{selectedDocument.reported_issue}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Client Dialog */}
      <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Novo Cliente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNewClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input
                  value={newClientForm.name}
                  onChange={(e) => setNewClientForm({...newClientForm, name: e.target.value})}
                  placeholder="Nome completo ou empresa"
                  required
                  data-testid="new-client-name-input"
                />
              </div>
              <div>
                <Label>NUIT</Label>
                <Input
                  value={newClientForm.nuit}
                  onChange={(e) => setNewClientForm({...newClientForm, nuit: e.target.value})}
                  placeholder="400123456"
                  data-testid="new-client-nuit-input"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={newClientForm.type} 
                  onValueChange={(v) => setNewClientForm({...newClientForm, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={newClientForm.phone}
                  onChange={(e) => setNewClientForm({...newClientForm, phone: e.target.value})}
                  placeholder="+258 84 123 4567"
                  data-testid="new-client-phone-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newClientForm.email}
                  onChange={(e) => setNewClientForm({...newClientForm, email: e.target.value})}
                  placeholder="email@exemplo.com"
                  data-testid="new-client-email-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Endereço</Label>
                <Input
                  value={newClientForm.address}
                  onChange={(e) => setNewClientForm({...newClientForm, address: e.target.value})}
                  placeholder="Morada completa"
                  data-testid="new-client-address-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewClientDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={savingClient} data-testid="save-new-client-btn">
                {savingClient ? 'A guardar...' : 'Criar Cliente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}
